<template>
  <Tab :tab="tab">
    <template #header-action>
      <FormulateInput
        type="button"
        :input-class="['btn-primary mb-0 group']"
        :disabled="isSubmitting"
        @click="triggerOnSubmit"
      >
        <span :class="{ hidden: isSubmitting }">Save changes</span>
        <lottie
          :options="LOADING_ANIMATION_OPTIONS"
          :style="{ width: '1.5rem' }"
          :class="{
            'hidden': !isSubmitting,
            'group-hover:hidden': isSubmitting,
          }"
        />
        <lottie
          :options="LOADING_ANIMATION_OPTIONS_DARK"
          :style="{ width: '1.5rem' }"
          :class="{
            'hidden': !isSubmitting,
            'hidden group-hover:block': isSubmitting,
          }"
        />
      </FormulateInput>
    </template>

    <template v-if="settings">
      <FormulateForm v-model="form" @submit="onSubmit">
        <FormulateInput v-show="false" ref="submitInput" type="submit" />

        <div class="grid grid-cols-2 gap-24">
          <!-- Styles -->
          <div class="col-span-1 flex flex-col gap-6">
            <h5>Styles</h5>

            <div class="grid grid-cols-2 gap-x-4 gap-y-10">
              <!-- Colour - primary -->
              <div class="col-span-2">
                <FormulateInput
                  name="colourPrimary"
                  label="Primary Colour"
                  validation="required"
                  validation-name="Primary colour"
                >
                  <template #element>
                    <TwitterPicker
                      v-if="colourPrimary"
                      id="colourPrimary"
                      v-model="colourPrimary"
                      name="colourPrimary"
                      :triangle="'hide'"
                      :default-colors="[
                        '#E66E00',
                        '#A10F20',
                        '#E5287C',
                        '#B639D6',
                        '#5C25D0',
                        '#12268E',
                        '#1860D4',
                        '#3B82F6',
                        '#619709',
                        '#0F8C7D',
                      ]"
                    />
                  </template>
                </FormulateInput>
              </div>
              <!-- /Colour - primary -->

              <!-- Input fields -->
              <div class="col-span-1">
                <FormulateInput
                  type="select"
                  name="layout"
                  label="Layout"
                  :options="LAYOUT_TYPES"
                  validation="required"
                  validation-name="School name"
                />
              </div>

              <div class="col-span-1">
                <FormulateInput
                  type="select"
                  name="theme"
                  label="Theme"
                  :options="THEME_TYPES"
                  validation="required"
                />
              </div>

              <div
                v-if="!isOldLayout"
                class="col-span-2 rounded-xl w-full bg-blue-100 flex items-center gap-4 p-4"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  class="w-4 h-4 flex-shrink-0"
                >
                  <path
                    fill-rule="evenodd"
                    d="M15 8A7 7 0 1 1 1 8a7 7 0 0 1 14 0ZM9 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM6.75 8a.75.75 0 0 0 0 1.5h.75v1.75a.75.75 0 0 0 1.5 0v-2.5A.75.75 0 0 0 8.25 8h-1.5Z"
                    clip-rule="evenodd"
                  />
                </svg>
                <p class="text-xs">
                  With this layout selected, you can optionally include one or
                  multiple admins to have their contact details listed along
                  side your contact form. Use the Admins tab to enable this.
                </p>
              </div>
              <!-- /Input fields -->
            </div>
          </div>
          <!-- /Styles -->

          <!-- Home page content -->
          <div class="col-span-1 flex flex-col gap-6">
            <h5>Content & Content styling</h5>

            <div class="flex flex-col gap-x-4 gap-y-6">
              <!-- Contact form -->
              <FormulateInput
                type="email"
                name="contactEmail"
                label="Contact form"
                :validation="isOldLayout ? null : 'bail|required|email'"
                :error-behavior="isOldLayout ? 'submit' : 'blur'"
                validation-name="Contact email"
                help="Enable your contact form by adding a valid contact email address."
                placeholder="admin@school.nz"
                :help-class="['mt-1']"
              />
              <!-- /Contact form -->

              <!-- Fonts & Menu layout -->
              <div
                v-show="isOldLayout"
                class="grid grid-cols-2 col-span-2 gap-4"
              >
                <div class="col-span-1">
                  <FormulateInput
                    type="select"
                    name="fontTitle"
                    label="Heading font"
                    :options="FONT_TITLE_TYPES"
                    validation="required"
                    validation-name="Heading font"
                  />
                </div>

                <div class="col-span-1">
                  <FormulateInput
                    type="select"
                    name="fontBody"
                    label="Body font"
                    :options="FONT_BODY_TYPES"
                    validation="required"
                    validation-name="Body font"
                  />
                </div>

                <div class="col-span-2">
                  <a @click="previewFonts"> Preview fonts </a>
                </div>
              </div>

              <div v-show="isOldLayout" class="col-span-1">
                <FormulateInput
                  type="select"
                  name="menu"
                  label="Menu style"
                  :options="MENU_TYPES"
                  validation="required"
                  validation-name="Menu style"
                />
              </div>
              <!-- /Fonts & Menu layout -->

              <!-- Layout3 specific -->
              <template>
                <!-- Connections feature -->
                <!-- "on your home page" must be conditional on layout3 selected! -->
                <FormulateInput
                  type="checkbox"
                  name="connectionsFeatureEnabled"
                  label="Enable Connections feature"
                  :help="
                    isOldLayout
                      ? 'Show or hide Connections in your public menu.'
                      : 'Show or hide Connections on your home page and in your public menu.'
                  "
                  :outer-class="['border-t border-truegray-300 pt-6']"
                  :help-class="['mt-1']"
                />
                <!-- /Connections feature -->
                <!-- Connections signup -->
                <FormulateInput
                  v-show="!isOldLayout"
                  type="checkbox"
                  name="connectionsSignupEnabled"
                  label="Display parent connection sign up"
                  :disabled="!form.connectionsFeatureEnabled"
                  help="Show or hide the parent sign up for CareerWise Connections on your home page."
                  :outer-class="['border-b border-truegray-300 pb-6']"
                  :element-class="[
                    !form.connectionsFeatureEnabled
                      ? 'opacity-20 cursor-not-allowed'
                      : '',
                  ]"
                  :label-class="[
                    !form.connectionsFeatureEnabled
                      ? 'text-truegray-300 cursor-not-allowed'
                      : '',
                  ]"
                  :help-class="[
                    'mt-1',
                    !form.connectionsFeatureEnabled ? 'text-truegray-300' : '',
                  ]"
                />
                <!-- /Connections signup -->

                <!-- Content selector -->
                <div v-show="!isOldLayout" class="w-full">
                  <p class="font-medium text-sm mb-2">
                    Select the content types you would like to appear on your
                    home page 'Explore' section.
                  </p>

                  <div
                    v-if="selectedContentItems.length"
                    class="mb-2 flex flex-wrap items-center gap-2"
                  >
                    <p class="pr-2 text-truegray-500">Selected:</p>
                    <div
                      v-for="(item, index) in selectedContentItems"
                      :key="index"
                      class="cursor-pointer flex items-center justify-center bg-primary rounded-full py-1 px-3 text-xs text-white border border-primary hover:text-primary hover:bg-white transition-all"
                      @click="unselectContentItem(item)"
                    >
                      {{ item.text }}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        class="w-3 h-3 ml-2"
                      >
                        <path
                          d="M5.28 4.22a.75.75 0 0 0-1.06 1.06L6.94 8l-2.72 2.72a.75.75 0 1 0 1.06 1.06L8 9.06l2.72 2.72a.75.75 0 1 0 1.06-1.06L9.06 8l2.72-2.72a.75.75 0 0 0-1.06-1.06L8 6.94 5.28 4.22Z"
                        />
                      </svg>
                    </div>
                  </div>

                  <div class="border border-truegray-300 rounded p-4">
                    <h6
                      class="text-sm mb-2 flex items-center cursor-pointer"
                      @click="setContentTypeOpen('posts')"
                    >
                      Post Types:
                      <svg
                        v-if="!contentTypeOpen.posts"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        class="w-4 h-4 ml-4"
                      >
                        <path
                          d="M8.75 3.75a.75.75 0 0 0-1.5 0v3.5h-3.5a.75.75 0 0 0 0 1.5h3.5v3.5a.75.75 0 0 0 1.5 0v-3.5h3.5a.75.75 0 0 0 0-1.5h-3.5v-3.5Z"
                        />
                      </svg>

                      <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        class="w-4 h-4 ml-4"
                      >
                        <path
                          d="M3.75 7.25a.75.75 0 0 0 0 1.5h8.5a.75.75 0 0 0 0-1.5h-8.5Z"
                        />
                      </svg>
                    </h6>
                    <div v-if="contentTypeOpen.posts">
                      <div class="flex flex-col space-y-2">
                        <FormulateInput
                          v-for="(type, i) in contentTypes.posts"
                          :key="i"
                          v-model="contentPostTypes"
                          type="checkbox"
                          :label="type.text"
                          :value="type.value"
                        />
                      </div>
                    </div>

                    <h6
                      class="text-sm mt-4 mb-2 flex items-center cursor-pointer border-t border-truegray-300 pt-4"
                      @click="setContentTypeOpen('tags')"
                    >
                      Tag Types (max 3):
                      <svg
                        v-if="!contentTypeOpen.tags"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        class="w-4 h-4 ml-4"
                      >
                        <path
                          d="M8.75 3.75a.75.75 0 0 0-1.5 0v3.5h-3.5a.75.75 0 0 0 0 1.5h3.5v3.5a.75.75 0 0 0 1.5 0v-3.5h3.5a.75.75 0 0 0 0-1.5h-3.5v-3.5Z"
                        />
                      </svg>

                      <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        class="w-4 h-4 ml-4"
                      >
                        <path
                          d="M3.75 7.25a.75.75 0 0 0 0 1.5h8.5a.75.75 0 0 0 0-1.5h-8.5Z"
                        />
                      </svg>
                    </h6>
                    <div v-if="contentTypeOpen.tags" class="">
                      <div
                        v-if="contentTypes.generalTags"
                        class="flex flex-col space-y-2"
                      >
                        <FormulateInput
                          v-for="(type, i) in contentTypes.generalTags"
                          :key="i"
                          v-model="contentTagTypes"
                          type="checkbox"
                          :disabled="
                            contentTagTypes.length >= 3 &&
                            !contentTagTypes.includes(type.value)
                          "
                          :label="type.text"
                          :value="type.value"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <!-- /Content selector -->
              </template>
              <!-- /Layout3 specific -->
            </div>
          </div>
        </div>
      </FormulateForm>
    </template>
  </Tab>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, nextTick } from 'vue'
import { useStore } from '../../../../../js/store'
import { EventBus } from '../../../../../js/plugins/event-bus.js'
import Tab from './slots/Tab.vue'
import { Twitter } from 'vue-color'
import {
  LOADING_ANIMATION_OPTIONS,
  LOADING_ANIMATION_OPTIONS_DARK,
  LAYOUT_TYPES,
  THEME_TYPES,
  LEXEND_KEY,
  FONT_TITLE_TYPES,
  FONT_BODY_TYPES,
  MENU_TYPES,
  CATEGORY_TYPES,
} from '../../../../../js/util/constants.js'

export default defineComponent({
  name: 'TabCustomisation',

  components: {
    Tab,
    TwitterPicker: Twitter,
  },

  props: {
    tab: {
      type: Object,
      required: true,
    },
  },

  setup() {
    const store = useStore()
    const settings = computed(() => store.getters['shared/getSettings'])
    const generalTags = computed(() => store.getters['shared/getTagsGeneral'])

    const NEW_LAYOUT_VALUE = LAYOUT_TYPES[LAYOUT_TYPES.length - 1].value
    const isOldLayout = computed(() => form.value.layout !== NEW_LAYOUT_VALUE)

    const form = ref({})

    watch(
      () => form.value.layout,
      (val) => {
        if (val === NEW_LAYOUT_VALUE) {
          // Set all fonts to Lexend
          const lexendTitleValue = FONT_TITLE_TYPES.find(
            (font) => font.label === LEXEND_KEY,
          )?.value
          const lexendBodyValue = FONT_BODY_TYPES.find(
            (font) => font.label === LEXEND_KEY,
          )?.value

          form.value.fontTitle = lexendTitleValue
          form.value.fontBody = lexendBodyValue

          // If we have no original values for these then it is a first time select of the new layout - add defaults.
          if (!contentPostTypes.value.length && !contentTagTypes.value.length) {
            contentPostTypes.value.push(1, 3)
          }
        }
      },
    )

    const colourPrimary = ref(null)
    watch(colourPrimary, (colour) => {
      if (colour?.hex) {
        form.value.colourPrimary = colour.hex
      }
    })

    const previewFonts = () => {
      EventBus.$emit('showModal', 'font-preview')
    }

    const contentTypeOpen = ref({
      posts: true,
      tags: false,
    })

    const contentTypes = computed(() => {
      return {
        posts: [...CATEGORY_TYPES],
        // TODO set these!!!
        generalTags: generalTags.value.map((tag) => ({
          text: tag.text,
          value: tag.value,
        })),
      }
    })

    const contentPostTypes = ref([])
    const contentTagTypes = ref([])

    const selectedContentItems = computed(() => {
      const postTypes = contentPostTypes.value
        ? contentPostTypes.value.map((value) => {
            return {
              text: contentTypes.value.posts.find(
                (type) => type.value === value,
              ).text,
              selectedItems: contentPostTypes.value,
              value,
            }
          })
        : []

      const tags = contentTagTypes.value
        ? contentTagTypes.value.map((value) => {
            return {
              text: contentTypes.value.generalTags.find(
                (type) => type.value === value,
              ).text,
              selectedItems: contentTagTypes.value,
              value,
            }
          })
        : []

      return [...postTypes, ...tags]
    })

    const unselectContentItem = (item) => {
      const index = item.selectedItems.indexOf(item.value)
      if (index !== -1) {
        item.selectedItems.splice(index, 1)
      }
    }

    const setContentTypeOpen = (key) => {
      // If the passed key is currently true, set all keys to false
      if (contentTypeOpen.value[key]) {
        Object.keys(contentTypeOpen.value).forEach((k) => {
          contentTypeOpen.value[k] = false
        })
      } else {
        // If the passed key is currently false, set all other keys to false and that one to true
        Object.keys(contentTypeOpen.value).forEach((k) => {
          contentTypeOpen.value[k] = k === key
        })
      }
    }

    const FORM_MAP = {
      colourPrimary: 'primary_colour',
      layout: 'customisation',
      theme: 'customisation',
      menu: 'customisation',
      fontTitle: 'font_title',
      fontBody: 'font_body',
      contactEmail: 'contact_email',
      connectionsFeatureEnabled: 'enable_connections_feature',
      connectionsSignupEnabled: 'enable_connections_signup',
    }

    const FORM_MAP_CUSTOM = {
      primary_colour: colourPrimary,
      customisation: {
        isNested: true,
        contentPostTypes: contentPostTypes,
        contentTags: contentTagTypes,
      },
    }

    const FONT_MAP = {
      fontTitle: FONT_TITLE_TYPES,
      fontBody: FONT_BODY_TYPES,
    }

    const mapFontsToValues = () => {
      Object.keys(FONT_MAP).forEach((type) => {
        const font = FONT_MAP[type].find(
          (font) => font.label === form.value[type],
        )

        if (font) {
          // Value is already a string "integer"
          form.value[type] = font.value
        }
      })
    }

    const getSelectedFontByType = (type) => {
      const font = FONT_MAP[type].find(
        (font) => font.value === form.value[type],
      )
      return font || null
    }

    const mapFormCustomInputs = () => {
      Object.keys(FORM_MAP_CUSTOM).forEach((key) => {
        const customInput = FORM_MAP_CUSTOM[key]

        if (customInput.isNested) {
          // Handle nested properties
          Object.keys(customInput).forEach((nestedKey) => {
            if (nestedKey !== 'isNested') {
              const nestedValue = settings.value.customisation[nestedKey]
              customInput[nestedKey].value = nestedValue
            }
          })
        } else {
          const value = settings.value[key]
          customInput.value = value
        }
      })
    }

    // NOTE: cast integers to strings because VueFormulate stupidly does
    // this without asking for select options, making comparison a huge pain
    // in the ass when you need to conditionally show various inputs.
    // We can map back on submit. Very stupid.
    const mapForm = () => {
      Object.keys(FORM_MAP).forEach((key) => {
        const value =
          FORM_MAP[key] === 'customisation'
            ? settings.value.customisation[key].toString()
            : settings.value[FORM_MAP[key]]

        form.value[key] = value
      })

      mapFontsToValues()
      mapFormCustomInputs()
    }

    const payload = computed(() => {
      const data = {}

      // Form inputs
      Object.keys(FORM_MAP).forEach((key) => {
        const FORM_KEY = FORM_MAP[key]
        const isCustom = FORM_KEY === 'customisation'
        const isFont = FONT_MAP.hasOwnProperty(key)
        const formKey = isCustom ? key : FORM_KEY
        const value = form.value[key]

        if (value !== undefined) {
          if (isFont) {
            const font = getSelectedFontByType(key)
            if (font) {
              data[formKey] = font.label
            }
          } else {
            // Convert customisation string "integers" to actual integers
            data[formKey] = isCustom ? parseInt(value) : value
          }
        }
      })

      // Custom inputs - only for "customisation"!!!
      Object.keys(FORM_MAP_CUSTOM).forEach((key) => {
        const customInput = FORM_MAP_CUSTOM[key]

        if (customInput.isNested) {
          Object.keys(customInput).forEach((nestedKey) => {
            if (nestedKey !== 'isNested') {
              const nestedValue = customInput[nestedKey]
              data[nestedKey] = nestedValue.value
            }
          })
        }
      })

      const REFRESH_KEYS = ['colourPrimary', 'fontTitle', 'fontBody']
      const refresh = REFRESH_KEYS.some((key) => {
        const KEY = FORM_MAP[key]
        return data[KEY] !== settings.value[KEY]
      })

      return {
        ...data,
        refresh,
      }
    })

    const submitInput = ref(null)
    const triggerOnSubmit = () => {
      nextTick(() => {
        if (submitInput.value) {
          const nativeSubmitElement = submitInput.value.$el.querySelector(
            'button[type="submit"]',
          )

          if (nativeSubmitElement) {
            nativeSubmitElement.click()
          }
        }
      })
    }

    const isSubmitting = ref(false)
    const onSubmit = async () => {
      isSubmitting.value = true
      await store.dispatch('shared/dispatchUpdateSettings', payload.value)
      isSubmitting.value = false
    }

    const init = async () => {
      mapForm()
      await nextTick()
    }

    onMounted(() => init())

    return {
      LOADING_ANIMATION_OPTIONS,
      LOADING_ANIMATION_OPTIONS_DARK,
      LAYOUT_TYPES,
      THEME_TYPES,
      FONT_TITLE_TYPES,
      FONT_BODY_TYPES,
      MENU_TYPES,
      settings,
      isOldLayout,
      colourPrimary,
      previewFonts,
      contentTypeOpen,
      contentTypes,
      contentPostTypes,
      contentTagTypes,
      selectedContentItems,
      unselectContentItem,
      setContentTypeOpen,
      form,
      submitInput,
      triggerOnSubmit,
      isSubmitting,
      onSubmit,
    }
  },
})
</script>
